import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Divider,
  InlineStack,
  Page,
  Spinner,
  Text,
} from '@shopify/polaris';
import type { BadgeProps } from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import type { EventType, SessionDetail, SessionRecipient, SessionStatus } from '../types';
import { signerColor } from '../multiSigner';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function sessionTone(s: SessionStatus): BadgeProps['tone'] {
  const map: Partial<Record<SessionStatus, BadgeProps['tone']>> = {
    PENDING: 'attention', VIEWED: 'info', SIGNED: 'success',
    DECLINED: 'critical', EXPIRED: 'warning',
  };
  return map[s];
}

const EVENT_LABELS: Partial<Record<EventType, { icon: string; text: string }>> = {
  SESSION_CREATED:   { icon: '📝', text: 'Session created' },
  EMAIL_SENT:        { icon: '📧', text: 'Signing email sent' },
  REMINDER_SENT:     { icon: '🔔', text: 'Reminder sent' },
  LINK_OPENED:       { icon: '🔗', text: 'Link opened' },
  DOCUMENT_VIEWED:   { icon: '👁', text: 'Document viewed' },
  FIELD_FILLED:      { icon: '✏️', text: 'Field filled' },
  SIGNATURE_DRAWN:   { icon: '✍', text: 'Signature drawn' },
  DOCUMENT_SIGNED:   { icon: '✅', text: 'Document signed' },
  DOCUMENT_DECLINED: { icon: '❌', text: 'Document declined' },
  PDF_GENERATED:     { icon: '📄', text: 'Signed PDF generated' },
  PDF_STORED:        { icon: '💾', text: 'PDF stored' },
  SHOPIFY_METAFIELD_SET: { icon: '🏪', text: 'Shopify order updated' },
  SESSION_EXPIRED:   { icon: '⏰', text: 'Session expired' },
  SESSION_CANCELLED: { icon: '🚫', text: 'Session cancelled' },
};

/** A signer's progress on a multi-signer document, in merchant-friendly words. */
function recipientBadge(r: SessionRecipient, docStatus: SessionStatus): { tone: BadgeProps['tone']; label: string } {
  if (r.status === 'SIGNED') return { tone: 'success', label: 'Signed' };
  if (r.status === 'DECLINED') return { tone: 'critical', label: 'Declined' };
  if (docStatus === 'EXPIRED' && (r.status === 'PENDING' || r.status === 'VIEWED')) return { tone: 'warning', label: 'Link expired' };
  if (docStatus === 'DECLINED') return { tone: undefined, label: 'Not needed' };
  if (r.status === 'VIEWED') return { tone: 'info', label: 'Opened — not signed yet' };
  if (r.status === 'PENDING') return { tone: 'attention', label: 'Email sent — their turn' };
  return { tone: undefined, label: 'Waiting for earlier signer' };
}

function fmt(iso: string | null) {
  if (!iso) return null;
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SessionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { request, getToken } = useApi();

  const [session, setSession] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [resending, setResending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Soft notices (e.g. the re-send cooldown) — shown in yellow, not as an error.
  const [warning, setWarning] = useState<string | null>(null);
  // Re-render every 30 s so the "Re-send available in X min" countdown stays current.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await request<SessionDetail>(`/sessions/${id}`);
      setSession(data);
      setNow(Date.now()); // fresh countdown right after loading (e.g. after a re-send)
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, request]);

  useEffect(() => { load(); }, [load]);

  // Email a fresh link to whoever's turn it is (or the one signer).
  const handleResend = useCallback(async () => {
    if (!id) return;
    setResending(true);
    setError(null);
    setNotice(null);
    setWarning(null);
    try {
      const res = await request<{ signerName: string; signerEmail: string }>(`/sessions/${id}/resend`, { method: 'POST' });
      setNotice(`A new signing link was emailed to ${res.signerName} (${res.signerEmail}). Their previous link no longer works.`);
      await load();
    } catch (e: any) {
      // The cooldown isn't an error: show it as a gentle notice.
      if (/re-send in/i.test(e.message ?? '')) setWarning(e.message);
      else setError(e.message);
    } finally {
      setResending(false);
    }
  }, [id, request, load]);

  const handleDownload = useCallback(async () => {
    if (!id) return;
    setDownloading(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/sessions/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('PDF not available');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signed-document-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setDownloading(false);
    }
  }, [id, getToken]);

  if (loading) {
    return (
      <Page backAction={{ content: 'Sessions', url: '/sessions' }} title="Session">
        <InlineStack align="center"><Spinner /></InlineStack>
      </Page>
    );
  }

  if (!session) {
    return (
      <Page backAction={{ content: 'Sessions', url: '/sessions' }} title="Not found">
        <Banner tone="critical">{error ?? 'Session not found'}</Banner>
      </Page>
    );
  }

  const fieldMap = new Map(session.template.fields.map((f) => [f.id, f]));
  // Multi-signer documents carry one recipient per signer; one-signer documents none.
  const recipients = session.recipients ?? [];
  const multi = recipients.length > 1;
  const signedCount = recipients.filter((r) => r.status === 'SIGNED').length;
  const inProgress = multi && (session.status === 'PENDING' || session.status === 'VIEWED' || session.status === 'EXPIRED');
  const current = recipients.find((r) => r.status === 'PENDING' || r.status === 'VIEWED');
  const nameAt = (pos?: number | null) => recipients.find((r) => r.position === pos)?.name;
  // Re-send waits 10 minutes after the last email to this signer (matches the server).
  const cooldownMin = current?.sentAt
    ? Math.ceil((10 * 60000 - (now - new Date(current.sentAt).getTime())) / 60000)
    : 0;
  // One-signer documents: re-send from here too (needed for documents sent without an order).
  const canResendSingle = !multi && (session.status === 'PENDING' || session.status === 'VIEWED' || session.status === 'EXPIRED');
  const lastEmailAt = Math.max(0, ...session.events
    .filter((e) => e.eventType === 'EMAIL_SENT')
    .map((e) => new Date(e.occurredAt).getTime()));
  const singleCooldownMin = lastEmailAt ? Math.ceil((10 * 60000 - (now - lastEmailAt)) / 60000) : 0;

  return (
    <Page
      title={session.template.name}
      titleMetadata={
        multi && (session.status === 'PENDING' || session.status === 'VIEWED') ? (
          <Badge tone="info">{`Signed ${signedCount} of ${recipients.length}`}</Badge>
        ) : (
          <Badge tone={sessionTone(session.status)}>
            {session.status.charAt(0) + session.status.slice(1).toLowerCase()}
          </Badge>
        )
      }
      backAction={{ content: 'Sessions', url: '/sessions' }}
      primaryAction={
        session.status === 'SIGNED'
          ? { content: 'Download signed PDF', loading: downloading, onAction: handleDownload }
          : undefined
      }
    >
      <BlockStack gap="500">
        {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
        {notice && <Banner tone="success" onDismiss={() => setNotice(null)}>{notice}</Banner>}
        {warning && <Banner tone="warning" onDismiss={() => setWarning(null)}>{warning}</Banner>}

        {/* ── Multi-signer: each signer's progress ────────────────────── */}
        {multi && (
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="headingSm" as="h2">Signers ({signedCount} of {recipients.length} signed)</Text>
                {inProgress && current && (
                  <Button loading={resending} onClick={handleResend} disabled={cooldownMin > 0}>
                    {cooldownMin > 0
                      ? `Re-send available in ${cooldownMin} min`
                      : `Re-send link to ${current.name}`}
                  </Button>
                )}
              </InlineStack>
              {recipients.map((r, i) => {
                const badge = recipientBadge(r, session.status);
                const when = r.signedAt ? `Signed ${fmt(r.signedAt)}`
                  : r.declinedAt ? `Declined ${fmt(r.declinedAt)}`
                  : r.viewedAt ? `Opened ${fmt(r.viewedAt)}`
                  : r.sentAt ? `Emailed ${fmt(r.sentAt)}` : '';
                return (
                  <div key={r.position}>
                    {i > 0 && <Divider />}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: i > 0 ? 12 : 0 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 12,
                        background: signerColor(r.position), color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{r.position}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text as="p" fontWeight="semibold">{r.name} <Text as="span" tone="subdued">· {r.role}</Text></Text>
                        <Text as="p" variant="bodySm" tone="subdued">{r.email}{when ? ` · ${when}` : ''}</Text>
                      </div>
                      <Badge tone={badge.tone}>{badge.label}</Badge>
                    </div>
                  </div>
                );
              })}
              {inProgress && current && (
                <Text as="p" variant="bodySm" tone="subdued">
                  Re-sending emails {current.name} a new link (free — it doesn't use a signing request). Their old link stops working.
                </Text>
              )}
            </BlockStack>
          </Card>
        )}

        {/* ── Signer + document info ──────────────────────────────────── */}
        <InlineStack gap="400" align="start" wrap>
          <div style={{ flex: 1, minWidth: 260 }}>
            <Card>
              <BlockStack gap="300">
                <Text variant="headingSm" as="h2">{multi ? 'Customer' : 'Signer'}</Text>
                <InfoRow label="Name" value={session.signerName} />
                <InfoRow label="Email" value={session.signerEmail} />
                <InfoRow
                  label="Order"
                  value={session.shopifyOrderNumber ? `#${session.shopifyOrderNumber}` : 'None — sent directly'}
                />
                {canResendSingle && (
                  <BlockStack gap="100">
                    <InlineStack>
                      <Button loading={resending} onClick={handleResend} disabled={singleCooldownMin > 0}>
                        {singleCooldownMin > 0
                          ? `Re-send available in ${singleCooldownMin} min`
                          : `Re-send link to ${session.signerName}`}
                      </Button>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Free — it doesn't use a signing request. The previous link stops working.
                    </Text>
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
          </div>

          <div style={{ flex: 1, minWidth: 260 }}>
            <Card>
              <BlockStack gap="300">
                <Text variant="headingSm" as="h2">Timeline</Text>
                <InfoRow label="Created" value={fmt(session.createdAt) ?? '—'} />
                {session.viewedAt && <InfoRow label="Viewed" value={fmt(session.viewedAt)!} />}
                {session.signedAt && <InfoRow label="Signed" value={fmt(session.signedAt)!} />}
                {session.declinedAt && <InfoRow label="Declined" value={fmt(session.declinedAt)!} />}
                {session.expiredAt && <InfoRow label="Expired" value={fmt(session.expiredAt)!} />}
                {session.signedPdfHash && (
                  <InfoRow
                    label="Document hash"
                    value={`${session.signedPdfHash.slice(0, 16)}…`}
                    mono
                  />
                )}
              </BlockStack>
            </Card>
          </div>
        </InlineStack>

        {/* ── Field values ────────────────────────────────────────────── */}
        {session.fieldValues.length > 0 && (
          <Card>
            <BlockStack gap="300">
              <Text variant="headingSm" as="h2">Fields filled in</Text>
              <div
                style={{
                  border: '1px solid var(--p-color-border)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                {session.fieldValues.map((fv, i) => {
                  const field = fieldMap.get(fv.fieldId);
                  const isSignature = field?.type === 'SIGNATURE' || field?.type === 'INITIALS';
                  return (
                    <div key={fv.id}>
                      {i > 0 && <Divider />}
                      <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        <div>
                          <Text variant="bodySm" fontWeight="semibold" as="span">
                            {field?.label ?? fv.fieldId.slice(0, 8)}
                          </Text>
                          {field && (
                            <Text variant="bodySm" tone="subdued" as="span"> · {field.type}</Text>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          {isSignature && fv.signatureDataUrl ? (
                            <img
                              src={fv.signatureDataUrl}
                              alt="Signature"
                              style={{ maxHeight: '40px', maxWidth: '160px', objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '2px' }}
                            />
                          ) : isSignature ? (
                            <Badge tone="success">Provided</Badge>
                          ) : fv.value === 'true' ? (
                            <Badge tone="success">Checked</Badge>
                          ) : (
                            <Text variant="bodySm" as="span">{fv.value ?? '—'}</Text>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </BlockStack>
          </Card>
        )}

        {/* ── Audit trail ─────────────────────────────────────────────── */}
        <Card>
          <BlockStack gap="300">
            <Text variant="headingSm" as="h2">Audit trail</Text>
            <div style={{ position: 'relative' }}>
              {/* vertical timeline line */}
              <div style={{ position: 'absolute', left: '11px', top: '8px', bottom: '8px', width: '2px', background: '#e5e7eb' }} />
              <BlockStack gap="0">
                {session.events.map((event, i) => {
                  const evCfg = EVENT_LABELS[event.eventType] ?? { icon: '•', text: event.eventType };
                  const isLast = i === session.events.length - 1;
                  const isSignedOrDeclined =
                    event.eventType === 'DOCUMENT_SIGNED' || event.eventType === 'DOCUMENT_DECLINED';
                  return (
                    <div
                      key={event.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        paddingBottom: isLast ? 0 : '16px',
                      }}
                    >
                      {/* dot */}
                      <div style={{
                        width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                        background: isSignedOrDeclined
                          ? (event.eventType === 'DOCUMENT_SIGNED' ? '#ecfdf5' : '#fef2f2')
                          : '#f3f4f6',
                        border: `2px solid ${isSignedOrDeclined
                          ? (event.eventType === 'DOCUMENT_SIGNED' ? '#059669' : '#ef4444')
                          : '#d1d5db'}`,
                        position: 'relative',
                        zIndex: 1,
                      }}>
                        {evCfg.icon}
                      </div>
                      <div style={{ paddingTop: '2px', flex: 1 }}>
                        <Text variant="bodyMd" fontWeight={isSignedOrDeclined ? 'semibold' : 'regular'} as="span">
                          {evCfg.text}{multi && nameAt(event.signerPosition) ? ` — ${nameAt(event.signerPosition)}` : ''}
                        </Text>
                        <InlineStack gap="300">
                          <Text variant="bodySm" tone="subdued" as="span">
                            {fmt(event.occurredAt)}
                          </Text>
                          {event.ipAddress && (
                            <Text variant="bodySm" tone="subdued" as="span">
                              · IP: {event.ipAddress}
                            </Text>
                          )}
                          {event.geoCity && (
                            <Text variant="bodySm" tone="subdued" as="span">
                              · {event.geoCity}{event.geoCountry ? `, ${event.geoCountry}` : ''}
                            </Text>
                          )}
                        </InlineStack>
                        {event.metadata && Object.keys(event.metadata).length > 0 && (
                          <Text variant="bodySm" tone="subdued" as="p">
                            {Object.entries(event.metadata as Record<string, unknown>)
                              .filter(([k]) => k !== 'storageKey')
                              .map(([k, v]) => `${k}: ${v}`)
                              .join(' · ')}
                          </Text>
                        )}
                      </div>
                    </div>
                  );
                })}
              </BlockStack>
            </div>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
      <Text variant="bodySm" tone="subdued" as="span">{label}</Text>
      <Text
        variant="bodySm"
        fontWeight="semibold"
        as="span"
        breakWord
      >
        <span style={mono ? { fontFamily: 'monospace', fontSize: '11px' } : undefined}>{value}</span>
      </Text>
    </div>
  );
}
